import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { apiErrorMessage, shopsService } from '../../services/api';
import { EmptyState, PageHeader, TableSkeleton } from '../../components/ui';
import { Shop } from '../../types';
import { getShopSlug } from '../../utils/shopUtils';

/**
 * Super-admin shop management.
 *
 * Creating, editing and deleting shops has always existed on the API, but the
 * only way to reach it was `scripts/create-shops.ts`.
 */

interface ShopForm {
  name: string;
  address: string;
  phoneNumber: string;
  email: string;
  description: string;
  isActive: boolean;
}

const EMPTY_FORM: ShopForm = {
  name: '',
  address: '',
  phoneNumber: '',
  email: '',
  description: '',
  isActive: true,
};

const toForm = (shop: Shop): ShopForm => ({
  name: shop.name,
  address: shop.address,
  phoneNumber: shop.phoneNumber ?? '',
  email: shop.email ?? '',
  description: shop.description ?? '',
  isActive: shop.isActive ?? true,
});

/** Optional fields go as undefined rather than "" so they clear cleanly. */
const toPayload = (form: ShopForm) => ({
  name: form.name.trim(),
  address: form.address.trim(),
  phoneNumber: form.phoneNumber.trim() || undefined,
  email: form.email.trim() || undefined,
  description: form.description.trim() || undefined,
  isActive: form.isActive,
});

const ShopsAdminPage: React.FC = () => {
  const [shops, setShops] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [editing, setEditing] = useState<Shop | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<ShopForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<Shop | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setShops(await shopsService.getAllShops());
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load shops.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (shop: Shop) => {
    setEditing(shop);
    setForm(toForm(shop));
    setDialogOpen(true);
  };

  const field =
    (key: keyof ShopForm) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      if (editing) {
        await shopsService.updateShop(editing.id, toPayload(form));
        setNotice(`${form.name.trim()} updated`);
      } else {
        await shopsService.createShop(toPayload(form));
        setNotice(`${form.name.trim()} created`);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the shop.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;

    try {
      await shopsService.deleteShop(pendingDelete.id);
      setNotice(`${pendingDelete.name} deleted`);
      await load();
    } catch (err) {
      // A shop with appointments, staff or settings still attached cannot be
      // removed; the API says so and that message is the useful one.
      setError(apiErrorMessage(err, 'Could not delete the shop.'));
    } finally {
      setPendingDelete(null);
    }
  };

  const canSave = form.name.trim().length > 0 && form.address.trim().length > 0;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <PageHeader
        title="Shops"
        subtitle="Every location on the platform. The slug is derived from the name and is what appears in shop URLs."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New shop
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {loading ? (
          <TableSkeleton rows={4} columns={4} />
        ) : shops.length === 0 ? (
          <EmptyState
            icon={<StorefrontOutlinedIcon />}
            title="No shops yet"
            description="Add the first location to start taking bookings."
            action={
              <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                New shop
              </Button>
            }
          />
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Shop</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shops.map((shop) => (
                  <TableRow key={shop.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{shop.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {shop.address}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        /shop/{getShopSlug(shop)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{shop.phoneNumber || '—'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {shop.email || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={shop.isActive ? 'Active' : 'Inactive'}
                        color={shop.isActive ? 'success' : 'default'}
                        variant={shop.isActive ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(shop)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setPendingDelete(shop)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'New shop'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              required
              fullWidth
              value={form.name}
              onChange={field('name')}
              helperText={
                form.name.trim()
                  ? `URL: /shop/${getShopSlug(form.name.trim())}`
                  : 'Also determines the shop URL.'
              }
            />
            <TextField
              label="Address"
              required
              fullWidth
              value={form.address}
              onChange={field('address')}
            />
            <TextField
              label="Phone number"
              fullWidth
              value={form.phoneNumber}
              onChange={field('phoneNumber')}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={form.email}
              onChange={field('email')}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={form.description}
              onChange={field('description')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, isActive: event.target.checked }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Create shop'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(pendingDelete)} onClose={() => setPendingDelete(null)}>
        <DialogTitle>Delete {pendingDelete?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This cannot be undone. A shop that still has appointments, staff or settings
            attached cannot be deleted — deactivate it instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        message={notice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Box sx={{ height: 24 }} />
    </Container>
  );
};

export default ShopsAdminPage;
